// eslint-disable-next-line import/no-extraneous-dependencies
import React, { useCallback } from 'react';
import {
	checklistClosedIndicator,
	ChecklistTitleWrapper,
	ChecklistTaskDoneIndicator,
	AccordionToggleButton,
} from './styled';
import CheckCircleIcon from '@atlaskit/icon/core/migration/status-success--check-circle';
import ChevronRightLargeIcon from '@atlaskit/icon/core/migration/chevron-right--chevron-right-large';
import { type Task } from '../..';
import Heading from '@atlaskit/heading';
import { fg } from '@atlaskit/platform-feature-flags';

const ANIMATION_DURATION = 200;
const ClosedIndicator = checklistClosedIndicator(ANIMATION_DURATION);

export interface Props extends Task {
	expanded: boolean;
	id: string;
	onClick: (id: string) => void;
}

export default ({ completed, title, id, expanded, onClick }: Props): React.JSX.Element => {
	const handleClick = useCallback(() => {
		onClick(expanded ? '' : id);
	}, [expanded, onClick, id]);

	// Space should only register on key up, but
	// requires prevent default to prevent page scroll
	// See https://hello.atlassian.net/wiki/spaces/~240225626/pages/1469726230/Accessibility+in+atlassiansox+checklist+and+Focus+Outline+Styles#Keyboard-Interaction-Behaviours
	const handleKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>) => {
		// Enter or Space key
		if (event.keyCode === 13 || event.keyCode === 32) {
			event.preventDefault();
			// Enter Key
			if (event.keyCode === 13) {
				handleClick();
			}
		}
	};

	const handleKeyUp = (event: React.KeyboardEvent<HTMLButtonElement>) => {
		// Space key
		if (event.keyCode === 32) {
			event.preventDefault();
			handleClick();
		}
	};

	return (
		<AccordionToggleButton
			onClick={handleClick}
			onKeyUp={handleKeyUp}
			onKeyDown={handleKeyDown}
			data-testid={`accordion-toggle-button-${id}`}
			aria-expanded={expanded}
			type="button"
		>
			<ChecklistTitleWrapper>
				<ChecklistTaskDoneIndicator done={completed} data-test-completed={completed} aria-hidden>
					<CheckCircleIcon color="currentColor" spacing="spacious" label="checked" />
				</ChecklistTaskDoneIndicator>
				<Heading
					size="xsmall"
					as={fg('jira_improve_qs_checklist_item_title_accessibility') ? 'div' : 'h3'}
				>
					{title}
				</Heading>
				<ClosedIndicator expanded={expanded} aria-hidden>
					<ChevronRightLargeIcon color="currentColor" label="chevron" size="small" />
				</ClosedIndicator>
			</ChecklistTitleWrapper>
		</AccordionToggleButton>
	);
};
